import { partKey } from "./msrExceptions";
import { PARTS_EXTRAS_KEY, RETIRED_SECTIONS, sidebarKey } from "./msrSections";
import { isMsrLockedError } from "./msrLock";

// LWI-1697 — structured save/submit problems. The API adds `problems`
// ([{section, line, line_id, item, field, message}]) to the 422 of submit and
// parts saves; these pure helpers sort failures, shape the lists and match
// lines to grid rows.

export const SERVER_FAILURE_MESSAGE =
  "Save failed on our side. Your changes were not saved. Please try again.";

export const PROBLEM_CAP = 10;

/** "locked" | "validation" | "server" | "other" (plain Errors and 4xx). */
export const saveFailureKind = (error) => {
  if (isMsrLockedError(error)) return "locked";
  if (error?.response?.status === 422) return "validation";
  // A request the app cancelled itself is not a failure on our side.
  if (error?.code === "ERR_CANCELED") return "other";
  if (
    error?.isAxiosError &&
    (!error.response || Number(error.response.status) >= 500)
  )
    return "server";
  return "other";
};

export const saveErrorMessage = (error, fallback) =>
  saveFailureKind(error) === "server"
    ? SERVER_FAILURE_MESSAGE
    : error?.response?.data?.message || error?.message || fallback;

/** The wrapper errors of the two tab savers carry `failures`; a server failure only when all are. */
export const isServerFailure = (error) =>
  Array.isArray(error?.failures) && error.failures.length
    ? error.failures.every((f) => saveFailureKind(f.error) === "server")
    : saveFailureKind(error) === "server";

export const problemsFromServer = (data) =>
  Array.isArray(data?.problems)
    ? data.problems.filter((p) => p && typeof p === "object")
    : [];

/** Server section → the save/marker key it files under. */
export const markerKeyOf = (section) =>
  RETIRED_SECTIONS[section] ? PARTS_EXTRAS_KEY : section;

const hasLine = (p) => p.line !== null && p.line !== undefined;
export const isLineProblem = (p) => hasLine(p);
export const isHeaderProblem = (p) => !hasLine(p) && !!p.field;
export const isSectionProblem = (p) => !hasLine(p) && !p.field;

export const formatProblem = (p) => {
  if (!hasLine(p)) return p.message;
  return p.item
    ? `Line ${p.line} (${p.item}): ${p.message}`
    : `Line ${p.line}: ${p.message}`;
};

export const capProblems = (list, cap = PROBLEM_CAP) => ({
  shown: list.slice(0, cap),
  more: Math.max(0, list.length - cap),
});

const rowItem = (row) =>
  row?.ITEM_NUMBER ?? row?.["ITEM NUMBER"] ?? row?.item_id ?? "";

/**
 * Map(partKey(item) → problems[]) of the line problems. Match order: line_id
 * against the row id (only when the item agrees, as rows added from the Parts
 * Manager can carry a parts_master id), then the item, then the position.
 */
export const matchProblemsToRows = (rows, problems) => {
  const list = Array.isArray(rows) ? rows : [];
  const map = new Map();
  (problems || []).filter(isLineProblem).forEach((p) => {
    let row = null;
    if (p.line_id !== null && p.line_id !== undefined) {
      row = list.find(
        (r) =>
          String(r?.id ?? r?.line_id ?? "") === String(p.line_id) &&
          (!p.item || partKey(rowItem(r)) === partKey(p.item))
      );
    }
    if (!row && p.item) row = list.find((r) => partKey(rowItem(r)) === partKey(p.item));
    // Position is a last resort for a line the server could not identify; a
    // row id that matched nothing means the list changed since — flag nothing
    // rather than the wrong row.
    const hadId = p.line_id !== null && p.line_id !== undefined;
    if (!row && !hadId && Number(p.line) >= 1) row = list[Number(p.line) - 1];
    if (!row) return;
    const k = partKey(rowItem(row));
    map.set(k, [...(map.get(k) || []), p]);
  });
  return map;
};

/** Sidebar tab → { server, problems } from the store's per-marker state. */
export const problemsBySidebarKey = (
  saveProblems = {},
  serverFailedSteps = []
) => {
  const out = {};
  const slot = (k) => {
    const t = sidebarKey(k);
    if (!out[t]) out[t] = { server: false, problems: [] };
    return out[t];
  };
  serverFailedSteps.forEach((k) => {
    slot(k).server = true;
  });
  Object.entries(saveProblems).forEach(([k, list]) => {
    if (list?.length) slot(k).problems.push(...list);
  });
  return out;
};

/** Every stored problem, flat, for the Shipment tab's fields and grid. */
export const allProblems = (saveProblems = {}) =>
  Object.values(saveProblems).flat();

/** First problem message for a header field (line-less problem), or undefined. */
export const headerProblemFor = (saveProblems, field) =>
  allProblems(saveProblems).find((p) => isHeaderProblem(p) && p.field === field)
    ?.message;
